<template>
  <div>
    <b-sidebar
      id="sidebar-new-appointment"
      sidebar-class="sidebar-xg"
      :visible="newAppointmentSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="(val) => mutateNewAppointmentSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4 v-if="newAppointmentSidebar.id && newAppointmentSidebar.id > 0">
            Editar compromisso
          </h4>
          <h4 v-else>Novo compromisso</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="resetForm"
        >
          <div v-if="isLead" class="alert alert-warning mb-2" role="alert">
            <div class="alert-body">
              <strong>AVISO:</strong> compromissos do tipo Momento X não estão
              disponíveis para leads
            </div>
          </div>
          <div
            v-if="hasEpRunning"
            class="alert alert-success mb-2"
            role="alert"
          >
            <div class="alert-body">
              Este cliente já possui um contrato de Entrevista Paga (EP) com
              status <strong>Rodando</strong>
            </div>
          </div>
          <div
            v-if="isProductionOfAnotherOwner"
            class="alert alert-warning mb-2"
            role="alert"
          >
            <div class="alert-body">
              Você está registrando uma atividade para um cliente pertencente a
              <strong>{{ otherOwnersLabel }}</strong>. Para fins de carteira,
              indicadores e produção, este registro será atribuído ao dono do
              cliente. Você continuará identificado como responsável pela
              execução.
            </div>
          </div>
          <b-row>
            <b-col cols="12">
              <template v-if="!hasMultipleCustomers">
                <dynamic-select
                  id="appointment-customer"
                  v-model="customer"
                  :label="appointmentParticipantLabel"
                  placeholder="Digite o nome do cliente, lead ou PN"
                  :options="customers"
                  :loading="loading.customers"
                  :disabled="isReschedule"
                  :class="getSelectErrorClass(v$.customer.$error)"
                  @find="findCustomers"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.customer.required.$invalid">
                    Você deve informar um {{ appointmentParticipantName }}
                  </span>
                </div>
              </template>
              <template v-else>
                <b-form-group label="Clientes" label-for="appointment-customer">
                  <v-select
                    id="appointment-customer"
                    v-model="customer"
                    multiple
                    :reduce="(customer) => customer.id"
                    :options="customers"
                    :loading="loading.customers"
                    label="name"
                    :class="getSelectErrorClass(v$.customer.$error)"
                  >
                    <template #no-options="{ search, searching, loading }">
                      Sem resultados
                    </template>
                  </v-select>
                  <div class="invalid-feedback">
                    <span v-if="v$.customer.required.$invalid">
                      Você deve informar um cliente
                    </span>
                  </div>
                </b-form-group>
              </template>
            </b-col>
          </b-row>
          <b-row v-if="canSuppressCustomerInvite">
            <b-col cols="12" class="mt-1">
              <b-form-checkbox
                id="appointment-suppress-customer-invite"
                v-model="suppressCustomerInvite"
              >
                {{ suppressCustomerInviteLabel }}
              </b-form-checkbox>
              <small v-if="suppressCustomerInvite" class="text-muted d-block mt-50">
                {{ suppressCustomerInviteHint }}
              </small>
            </b-col>
          </b-row>
          <div
            v-if="isReschedule && editingSuppressedInvite"
            class="alert alert-info mt-1 mb-0"
            role="alert"
          >
            <div class="alert-body">
              Compromisso criado sem convite ao cliente. Editar não envia
              convite ao cliente.
            </div>
          </div>
          <b-row v-if="!hasEmail && !customerInviteSuppressed">
            <b-col cols="12" class="mt-1">
              <b-form-group
                :label="`E-mail do ${appointmentParticipantName}`"
                label-for="appointment-customer-email"
              >
                <b-form-input
                  id="appointment-customer-email"
                  v-model="customerEmail"
                  type="email"
                  :class="{ 'is-invalid': v$.customerEmail.$error }"
                />
              </b-form-group>
              <div class="invalid-feedback">
                <span v-if="v$.customerEmail.required.$invalid">
                  Você deve informar um e-mail para o {{ appointmentParticipantName }}
                </span>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="mt-1">
              <b-form-group
                label="PN responsável"
                label-for="appointment-consultant"
              >
                <v-select
                  id="appointment-consultant"
                  v-model="consultant"
                  disabled
                  :reduce="(consultants_list) => consultants_list.id"
                  :options="consultants"
                  :loading="loading.consultants"
                  label="name"
                  :class="getSelectErrorClass(v$.consultant.$error)"
                >
                  <template #no-options="{ search, searching, loading }">
                    Sem resultados
                  </template>
                </v-select>
                <div class="invalid-feedback">
                  <span v-if="v$.consultant.required.$invalid">
                    Você deve informar um PN responsável
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" md="12" class="mt-1">
              <b-form-group
                label="Tipo de compromisso"
                label-for="appointment-type"
              >
                <v-select
                  id="appointment-type"
                  v-model="eventType"
                  :reduce="(types_list) => types_list.id"
                  :options="eventTypes"
                  :loading="loading.eventTypes"
                  :selectable="disableItem"
                  :disabled="isReschedule"
                  label="description"
                  :class="getSelectErrorClass(v$.eventType.$error)"
                >
                  <template #no-options="{ search, searching, loading }">
                    Sem resultados
                  </template>
                </v-select>
                <div class="invalid-feedback">
                  <span v-if="v$.eventType.required.$invalid">
                    Você deve informar o tipo de compromisso
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          
          <b-row>
            <b-col cols="12" md="12" class="mt-1">
              <b-form-group
                label="PN especialista"
                label-for="appointment-consultant-specialist"
              >
                <v-select
                  id="appointment-consultant-specialist"
                  v-model="consultantSpecialistId"
                  :reduce="(consultant) => consultant.id"
                  :options="specialistConsultantsList"
                  :loading="loading.specialistConsultants"
                  :disabled="!eventType"
                  label="displayName"
                  placeholder="Selecione um PN especialista"
                >
                  <template #no-options="{ search, searching, loading }">
                    {{
                      !eventType
                        ? "Selecione um tipo de compromisso primeiro"
                        : "Sem resultados"
                    }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="12" class="mt-1" v-if="selectedConsultant">
              <b-card
                :class="`pn-spec-card-${
                  skin === 'dark' ? 'dark' : 'light'
                } mb-0`"
                no-body
              >
                <b-card-body>
                  <b-row>
                    <b-col cols="8">
                      <h5 class="pn-name">{{ selectedConsultant.name }}</h5>
                      <small>Nível: {{ selectedConsultant.role_name }}</small>
                    </b-col>
                    <b-col cols="4">
                      <div
                        class="star-box d-flex justify-content-end align-items-center"
                      >
                        <img
                          class="star-icon me-1"
                          src="@/assets/images/svg/icn_star_active.svg"
                          alt="Avaliação"
                          loading="lazy"
                        />
                        <h5 class="mb-0 me-1">
                          {{
                            selectedConsultant.specialist_score_average || "0.0"
                          }}
                        </h5>
                        <small
                          >({{
                            selectedConsultant.specialist_evaluations_quantity ||
                            0
                          }})</small
                        >
                      </div>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col cols="12">
                      <div class="card-middle-line"></div>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col cols="6">
                      <small>Liderança</small>
                      <h5 class="pn-name mb-0">
                        {{ selectedConsultant.digital_franchise_name ? (selectedConsultant.digital_franchise_name | franchiseName) : "N/A" }}
                      </h5>
                    </b-col>
                    <b-col cols="6">
                      <small>Escritório</small>
                      <h5 class="pn-name mb-0">
                        {{
                          selectedConsultant.physical_franchise_name || "N/A"
                        }}
                      </h5>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="6" class="mt-1">
              <b-form-group
                label="Data de início"
                label-for="appointment-start-date"
              >
                <flat-pickr
                  id="appointment-start-date"
                  v-model="appointmentStart"
                  class="form-control"
                  :config="flatPickrConfig"
                  :class="{
                    'is-invalid': v$.appointmentStart.$error,
                  }"
                  @input="setEndDate"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.appointmentStart.required.$invalid">
                    Você deve informar uma data de início
                  </span>
                  <span v-else-if="v$.appointmentStart.notPast.$invalid">
                    {{ startInPastMessage }}
                  </span>
                </div>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" class="mt-1">
              <b-form-group
                label="Data de término"
                label-for="appointment-end-date"
              >
                <flat-pickr
                  id="appointment-end-date"
                  v-model="appointmentEnd"
                  class="form-control"
                  :config="flatPickrConfig"
                  :class="{
                    'is-invalid': v$.appointmentEnd.$error,
                  }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.appointmentEnd.required.$invalid">
                    Você deve informar uma data de término
                  </span>
                  <span v-else-if="v$.appointmentEnd.valid.$invalid">
                    Você deve informar uma data superior a de início
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" md="12" class="mt-1">
              <b-form-group
                label="Formato de reunião"
                label-for="appointment-meeting-format"
              >
                <v-select
                  id="appointment-meeting-format"
                  v-model="meetingFormat"
                  :reduce="(format_list) => format_list.id"
                  :options="appointmentMeetingFormatTypes"
                  :loading="loading.meetingFormatTypes"
                  label="description"
                  :class="getSelectErrorClass(v$.meetingFormat.$error)"
                >
                  <template #no-options="{ search, searching, loading }">
                    Sem resultados
                  </template>
                </v-select>
                <div class="invalid-feedback">
                  <span v-if="v$.meetingFormat.required.$invalid">
                    Você deve informar o formato de reunião
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="mt-1" v-if="meetingFormat">
              <b-form-group
                :label="meetingFormat === 'in_pearson' ? 'Endereço da reunião' : 'Link da reunião'"
                label-for="appointment-room-link"
              >
                <b-form-input
                  id="appointment-room-link"
                  v-model="roomLink"
                  :class="{
                    'is-invalid': v$.roomLink.$error,
                  }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.roomLink.required.$invalid">
                    Você deve informar o {{ meetingFormat === 'in_pearson' ? 'endereço' : 'link' }} da reunião
                  </span>
                  <span v-else-if="v$.roomLink.validUrl.$invalid">
                    Você deve informar uma URL válida para a reunião online
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="mt-1">
              <b-form-group
                label="Descrição"
                label-for="appointment-description"
              >
                <b-form-textarea
                  id="appointment-description"
                  v-model="description"
                  :class="{ 'is-invalid': v$.description.$error }"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <!-- Form Actions -->
          <div class="d-flex mt-2 justify-content-between">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :disabled="saving"
              variant="outline-primary"
              class="lead-buttons"
              @click="hide"
            >
              Voltar
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :disabled="saving"
              variant="primary"
              type="submit"
              class="lead-buttons"
            >
              {{ saving ? "Salvando..." : "Salvar" }}
            </b-button>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import {
  BSidebar,
  BForm,
  BFormGroup,
  BButton,
  BFormTextarea,
  BFormInput,
  BFormCheckbox,
  BCol,
  BRow,
} from 'bootstrap-vue'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import flatPickr from 'vue-flatpickr-component'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import DynamicSelect from '@/modules/shared/components/DynamicSelect.vue'
import { getVueSelectErrorClass } from '@/helpers/validators'
import { dateTimeToString } from '@/helpers/converters'
import newAppointmentForm from '../mixins/newAppointmentForm'
import * as types from '../store/types'

// Estado, carregamento das opções, validação e envio: `mixins/newAppointmentForm` (compartilhado com a página
// cheia de Novo compromisso). Aqui fica só o que é da sidebar: abrir/fechar, reagendamento e presets.
export default {
  components: {
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormTextarea,
    BFormInput,
    BFormCheckbox,
    BCol,
    BRow,
    vSelect,
    DynamicSelect,
    flatPickr,
  },
  directives: {
    Ripple,
  },
  mixins: [newAppointmentForm],
  data() {
    return {
      flatPickrConfig: {
        dateFormat: 'd/m/Y H:i',
        enableTime: true,
        locale: Portuguese,
        minDate: 'today',
      },
    }
  },
  computed: {
    ...mapGetters({
      newAppointmentSidebar: types.NEW_APPOINTMENT_SIDEBAR,
    }),
    isReschedule() {
      return this.newAppointmentSidebar.id && this.newAppointmentSidebar.id > 0
    },
  },
  methods: {
    ...mapMutations({
      mutateNewAppointmentSidebar: types.MUTATE_NEW_APPOINTMENT_SIDEBAR,
    }),
    ...mapActions({
      getAppointment: types.GET_APPOINTMENT,
    }),
    onShow() {
      this.mutateCustomers(undefined)
      this.consultant = this.user?.appify_consultant_id
      if (this.isReschedule) {
        this.getAppointment(this.newAppointmentSidebar.id)
          .then(resp => {
            const { data } = resp
            if (data) {
              this.id = data.id
              const customer = {
                id: data.customer_id,
                name: data.customer_name,
                email: data.customer_email,
              }
              this.mutateCustomers([customer])
              this.customer = data.customer_id
              this.eventType = data.event_type
              this.meetingFormat = data.meeting_format
              this.consultant = data.consultant_id

              if (data.event_type) {
                this.loadSpecialistConsultants(data.event_type).then(() => {
                  this.consultantSpecialistId = data.consultant_specialist_id
                })
              } else {
                this.consultantSpecialistId = undefined
              }
              this.appointmentStart = dateTimeToString(data.appointment_start)
              this.originalAppointmentStart = this.appointmentStart
              this.appointmentEnd = dateTimeToString(data.appointment_end)
              this.roomLink = data.meeting_room_link
              this.description = data.description
              this.editingSuppressedInvite = !!data.suppress_customer_invite
              this.v$.$touch()
            }
          })
          .catch(() => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: 'Oops!',
                text: 'Ocorreu um erro ao carregar o compromisso para edição. Entre em contato com o setor de TI.',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
      }
      const presetCustomers = this.newAppointmentSidebar.customers || []
      const customerId = this.newAppointmentSidebar.customer_id
        || (presetCustomers.length === 1 ? presetCustomers[0].id : undefined)
      if (customerId) {
        this.fetchAppointmentCustomer(customerId)
          .then(customer => {
            if (customer) {
              this.applyPresetEventType()
            }
          })
      } else if (presetCustomers.length > 0) {
        this.mutateCustomers(presetCustomers)
        this.customer = _.map(presetCustomers, 'id')
        this.applyPresetEventType()
      } else {
        this.applyPresetEventType()
      }
    },
    applyPresetEventType() {
      if (this.isReschedule || !this.newAppointmentSidebar.eventType) return
      this.eventType = this.newAppointmentSidebar.eventType
    },
    async onSubmit() {
      this.refreshNow()
      const isFormCorrect = await this.v$.$validate()
      if (!isFormCorrect) return
      if (!(await this.confirmOtherOwnerProduction())) return
      this.saving = true

      this.saveAppointment()
        .then(
          () => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: 'Sucesso',
                text: 'Compromisso salvo com sucesso!',
                icon: 'CoffeeIcon',
                variant: 'success',
              },
            })
            this.mutateNewAppointmentSidebar({ visible: false })
            this.newAppointmentSidebar.saveAction()
          },
          err => {
            this.notifyAppointmentSaveError(err)
          },
        )
        .finally(() => {
          this.saving = false
        })
    },
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError)
    },
    setEndDate() {
      if (this.appointmentStart) {
        const time = moment(this.appointmentStart, 'DD/MM/YYYY hh:mm').add(1, 'h')
        this.appointmentEnd = time.toDate()
      }
    },
    clear() {
      this.resetAppointmentForm()
      this.mutateNewAppointmentSidebar({
        customer_id: undefined,
        customers: [],
        eventType: undefined,
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.sidebar-xg {
  width: 36rem;
}

.lead-buttons {
  width: 10rem;
}

.pn-spec-card {
  &-dark {
    background-color: rgba(22, 29, 49, 0.3) !important;
    border: 1px solid #404656 !important;

    .card-middle-line {
      width: 100%;
      border: 1px solid #404656 !important;
      margin: 1rem 0;
    }
  }

  &-light {
    background-color: #f8f8f8 !important;
    border: 1px solid #d8d6de !important;

    .card-middle-line {
      width: 100%;
      border: 1px solid #d8d6de !important;
      margin: 1rem 0;
    }
  }
}

.pn-name {
  font-weight: bold !important;
}

.star-box {
  align-items: center;

  h5 {
    margin-right: 0.25rem;
    line-height: normal;
    font-weight: bold;
  }

  small {
    font-size: 1.07rem;
  }

  .star-icon {
    width: 20px;
    height: 20px;
    margin-right: 0.25rem;
  }
}
</style>
